import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Search } from 'lucide-react';
import ProfileAvatar from '../components/ProfileAvatar';

interface FollowPersonData {
  id: string;
  name: string;
  image: string;
  role: string;
  iwi: string;
  hapu: string;
  location: string;
  shortBio: string;
  focus: string;
}

const followPeople: FollowPersonData[] = [
  {
    id: 'billy-turner',
    name: 'Billy Turner',
    image: '/api/placeholder/300/300',
    role: 'Business Leader & Sovereignty Advocate',
    iwi: 'Ngāti Tūwharetoa',
    hapu: 'Ngāti Tūrangitukua',
    location: 'Taupō, Central North Island',
    shortBio: 'Successful entrepreneur advocating for Māori economic sovereignty and jurisdictional independence through business excellence.',
    focus: 'Economic sovereignty and business development'
  },
  {
    id: 'peter-martin',
    name: 'Peter Martin',
    image: '/api/placeholder/300/300',
    role: 'Common Law Educator & Legal Rights Advocate',
    iwi: 'Ngāti Manu',
    hapu: 'Te Uri-o-Hau',
    location: 'Auckland, Tāmaki Makaurau',
    shortBio: 'Educates communities on common law principles and issues diplomatic immunity cards through the Common Law Embassy NZ.',
    focus: 'Common law education and legal sovereignty'
  },
  {
    id: 'new-zealand-rangers',
    name: 'Māori Ranger Security Division',
    image: '/api/placeholder/300/300',
    role: 'Hapū Protection & Security',
    iwi: 'Multi-iwi collective',
    hapu: 'Supporting all hapū',
    location: 'Nationwide, Aotearoa',
    shortBio: 'Protecting hapū rights and sovereignty through community security services and customary law enforcement.',
    focus: 'Hapū security and customary rights protection'
  },
  {
    id: 'johnny-tupo',
    name: 'Johnny "Two Guns" Tupo',
    image: '/api/placeholder/300/300',
    role: 'Direct Action Activist',
    iwi: 'Ngāti Tuwharetoa',
    hapu: 'Ngāti Hinemihi',
    location: 'Rotorua, Bay of Plenty',
    shortBio: 'Known for direct action approaches to sovereignty advocacy, earning his nickname through fearless confrontation of authority.',
    focus: 'Direct action and confrontational activism'
  },
  {
    id: 'forestry-hilton',
    name: 'Forestry Hilton',
    image: '/api/placeholder/300/300',
    role: 'Alternative Banking Systems Developer',
    iwi: 'Ngāti Awa',
    hapu: 'Ngāti Pūkeko',
    location: 'Whakatāne, Bay of Plenty',
    shortBio: 'Developing alternative banking systems that challenge traditional financial structures and promote Māori economic independence.',
    focus: 'Alternative banking and financial systems'
  },
  {
    id: 'kiri-campbell',
    name: 'Kiri Campbell',
    image: '/api/placeholder/300/300',
    role: 'Banking System Reformer & Community Advocate',
    iwi: 'Taranaki',
    hapu: 'Ngāti Ruanui',
    location: 'Hawera, Taranaki',
    shortBio: 'Courageous advocate who challenged the banking system to expose institutional practices and inspire community discussion about monetary reform.',
    focus: 'Banking system reform and community education'
  },
  {
    id: 'matt-tarawa',
    name: 'Matt Tarawa',
    image: '/api/placeholder/300/300',
    role: 'Sovereignty Strategist',
    iwi: 'Ngāti Kahungunu',
    hapu: 'Ngāti Kere',
    location: 'Hastings, Hawke\'s Bay',
    shortBio: 'Strategic thinker focused on practical pathways to sovereignty, developing frameworks for hapū self-determination.',
    focus: 'Sovereignty strategy and self-determination'
  },
  {
    id: 'joseph-murphy',
    name: 'Joseph Murphy',
    image: '/api/placeholder/300/300',
    role: 'Chief Justice, Nga Tikanga Law Society',
    iwi: 'Ngāpuhi',
    hapu: 'Ngāti Hine',
    location: 'Kaikohe, Northland',
    shortBio: 'Former leader of the Nga Tikanga Law Society, advocating for Māori legal systems and issuing Māori driver licenses.',
    focus: 'Māori legal systems and parallel governance'
  },
  {
    id: 'peter-singh',
    name: 'Peter Singh',
    image: '/api/placeholder/300/300',
    role: 'Ko Hui o Rau Coordinator',
    iwi: 'Multi-cultural',
    hapu: 'Supporting tino rangatiratanga',
    location: 'Wellington, Te Whanganui-a-Tara',
    shortBio: 'Leading Ko Hui o Rau organization that provides support and resources for tino rangatiratanga movements across Aotearoa.',
    focus: 'Tino rangatiratanga support and coordination'
  }
];

const Follow = () => {
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.title = 'People of Interest | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  // Filter people based on search term
  const filteredPeople = followPeople.filter(person => 
    person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.iwi.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.hapu.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.shortBio.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.focus.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            People of Interest
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto opacity-90">
            Learn about influential advocates, activists, and leaders who are advancing customary rights, 
            sovereignty, and supporting hapū across Aotearoa. Good people for the cause to follow and read up on.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search people of interest..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Community Leaders Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-header">Notable Advocates & Activists</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From constitutional lawyers to direct action activists, banking system challengers to cultural preservationists - 
              learn about diverse voices who have made significant contributions to advancing Māori sovereignty and customary rights.
            </p>
            {searchTerm && (
              <div className="mt-4 text-lg text-gray-600">
                Showing {filteredPeople.length} {filteredPeople.length === 1 ? 'result' : 'results'} 
                {searchTerm && ` for "${searchTerm}"`}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {filteredPeople.length > 0 ? (
              filteredPeople.map((person) => (
              <div key={person.id} className="card group hover:scale-105 transition-all duration-300">
                {/* Profile Image */}
                <div className="relative mb-6">
                  <ProfileAvatar 
                    name={person.name} 
                    size="md" 
                    className="mx-auto"
                  />
                </div>

                {/* Profile Info */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{person.name}</h3>
                  <p className="text-ocean-teal font-medium text-sm mb-2">{person.role}</p>
                  
                  {/* Location & Iwi Info */}
                  <div className="text-xs text-gray-500 mb-3">
                    <div className="flex items-center justify-center mb-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {person.location}
                    </div>
                    <div className="cultural-border bg-gray-50 px-2 py-1 rounded-full inline-block">
                      {person.iwi} | {person.hapu}
                    </div>
                  </div>

                  {/* Focus Area */}
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-ocean-teal/10 text-ocean-teal text-xs rounded-full">
                      {person.focus}
                    </span>
                  </div>
                </div>

                {/* Short Bio */}
                <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                  {person.shortBio}
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Link
                    to={`/profile/${person.id}`}
                    className="btn-primary text-sm py-2 px-3 group-hover:shadow-lg transition-shadow inline-flex items-center justify-center"
                  >
                    View Profile
                    <ArrowRight className="ml-2 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Results Found</h3>
                <p className="text-gray-500">
                  Try adjusting your search terms or browse all people by clearing the search.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Areas of Focus
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="cultural-border bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Legal Advocacy</h3>
              <p className="text-gray-600 text-sm">Constitutional law, Treaty rights, and legal sovereignty</p>
            </div>
            <div className="cultural-border bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Direct Action</h3>
              <p className="text-gray-600 text-sm">Confrontational activism and immediate intervention</p>
            </div>
            <div className="cultural-border bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Financial Reform</h3>
              <p className="text-gray-600 text-sm">Banking alternatives and economic sovereignty</p>
            </div>
            <div className="cultural-border bg-gray-50 p-6 rounded-lg text-center">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Cultural Protection</h3>
              <p className="text-gray-600 text-sm">Traditional knowledge and hapū security</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Follow;